import { DEFAULT_ME_FIELDS, type MeQueryData, type WorkspaceQueryData } from '@cheqroom/api';
import {
	type HydrateSessionInput,
	hydrateSessionQueries as hydrateSharedSessionQueries,
	type SessionState,
	useSessionQueries,
} from '@cheqroom/hooks';
import { createContext, FC, ReactNode, useContext, useMemo } from 'react';

import { queryClient } from '../services/react-query';

/**
 * The web session, on the shared `@cheqroom/hooks` session layer (the same one mobile's
 * `SessionProvider` uses), with the workspace id from `global.central`.
 *
 * The legacy shell owns the session: it loads it first (`users.me` → `central._loadUser` /
 * `_loadGroup`) and hydrates these queries with that response ({@link hydrateSessionQueries}).
 * React only reads the cache (`fetch: false`), so it never refetches and replaces that data, and
 * it follows every `window.global.permissionHandler` rebuild.
 */

/** The workspace fields the permission hooks read. */
export const SESSION_WORKSPACE_FIELDS = ['profile', 'limits'] as const;

type SessionValue = SessionState<MeQueryData, WorkspaceQueryData<(typeof SESSION_WORKSPACE_FIELDS)[number]>>;

const SessionContext = createContext<SessionValue | null>(null);

export const SessionProvider: FC<{ children?: ReactNode }> = ({ children }) => {
	// The workspace id comes from the hydrated `users.me` group, so it follows legacy group loads.
	const { user, workspace } = useSessionQueries({
		meFields: DEFAULT_ME_FIELDS,
		workspaceFields: SESSION_WORKSPACE_FIELDS,
		fetch: false,
	});

	const value = useMemo<SessionValue>(
		() => (user ? { status: 'signedIn', user, workspace } : { status: 'loading', user: null, workspace: null }),
		[user, workspace]
	);

	return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

/** For apps without the legacy shell (the booking portal): they supply the session they already hold. */
export const SessionValueProvider: FC<{ value: SessionValue; children?: ReactNode }> = ({ value, children }) => (
	<SessionContext.Provider value={value}>{children}</SessionContext.Provider>
);

export type { SessionValue };

/** `{ user, workspace }` are `null` until the legacy shell has hydrated them. */
export const useSession = (): SessionValue => {
	const context = useContext(SessionContext);
	if (!context) throw new Error('useSession must be used within a SessionProvider');
	return context;
};

/**
 * Hydrate the session queries from the legacy `users.me` response. `central.js` calls this wherever
 * it (re)builds `permissionHandler`, so both follow the same data. Pass `group` alone when only the
 * workspace part was reloaded.
 */
export const hydrateSessionQueries = ({ user, group }: Omit<HydrateSessionInput, 'meFields' | 'workspaceFields'>) =>
	hydrateSharedSessionQueries(queryClient, {
		user,
		group,
		meFields: DEFAULT_ME_FIELDS,
		workspaceFields: SESSION_WORKSPACE_FIELDS,
	});
